import { toast } from "sonner";
import { TOAST_ERROR_DURATION_MS, TOAST_SUCCESS_DURATION_MS } from "@/lib/toast-durations";

// Unique toast ids; crypto.randomUUID() is unavailable on Fleet's plain-HTTP origin.
let releaseToastSeq = 0;

export interface ReleaseToast {
  /** sonner toast id — pass to the finish* helpers to morph this toast in place. */
  id: string;
  machineName: string;
  /** Stop the elapsed timer. Call exactly once, before finishing. */
  stop: () => void;
}

// Open a "Releasing <name>" loading toast that ticks elapsed seconds; resolve
// with finishReleaseSuccess/Error.
export function startReleaseToast(machineName: string): ReleaseToast {
  releaseToastSeq += 1;
  const id = `release-${releaseToastSeq}`;
  const startedAt = performance.now();

  const render = () => {
    const elapsedSeconds = Math.floor((performance.now() - startedAt) / 1000);
    toast.loading(`Releasing ${machineName}`, {
      id,
      description: `Running in parallel — ${elapsedSeconds}s elapsed`,
      // Stay open + unswipeable while in flight; the finish* helpers restore both.
      duration: Number.POSITIVE_INFINITY,
      dismissible: false,
    });
  };

  render();
  const timer = setInterval(render, 500);
  return {
    id,
    machineName,
    stop: () => clearInterval(timer),
  };
}

export function finishReleaseSuccess(id: string, machineName: string): void {
  toast.success(`Released ${machineName}`, {
    id,
    // Explicit undefined clears the loading line; sonner merges by id and keeps omitted keys.
    description: undefined,
    duration: TOAST_SUCCESS_DURATION_MS,
    dismissible: true,
  });
}

export function finishReleaseError(id: string, machineName: string, message: string): void {
  toast.error(`Failed to release ${machineName}`, {
    id,
    description: message,
    duration: TOAST_ERROR_DURATION_MS,
    dismissible: true,
  });
}

/** One row of a batch release result. `name` is the resolved display name. */
export interface BatchReleaseResult {
  ok: boolean;
  name: string;
  error: string | null;
}

/** Morph the batch toast in place into one consolidated success/warning/error result. */
export function finishBatchRelease(id: string, results: BatchReleaseResult[]): void {
  const total = results.length;
  const released = results.filter((result) => result.ok);
  const failed = results.filter((result) => !result.ok);

  if (failed.length === 0) {
    toast.success(`Released ${released.length} machines`, {
      id,
      description: released.map((result) => result.name).join(", "),
      duration: TOAST_SUCCESS_DURATION_MS,
      dismissible: true,
    });
  } else if (released.length === 0) {
    toast.error(`Failed to release ${total} machines`, {
      id,
      description: failed
        .map((result) => `${result.name}: ${result.error ?? "Release failed"}`)
        .join("\n"),
      duration: TOAST_ERROR_DURATION_MS,
      dismissible: true,
    });
  } else {
    toast.warning(`Released ${released.length} of ${total} machines`, {
      id,
      description: `Failed: ${failed.map((result) => result.name).join(", ")}`,
      duration: TOAST_ERROR_DURATION_MS,
      dismissible: true,
    });
  }
}
